import { createPopper, flip, Instance as PopperInstance, preventOverflow } from '@popperjs/core';
import classNames from 'classnames';
import { compact } from 'lodash';
import { useEffect, useRef } from 'react';
import Portal from '../../components/Portal/Portal';
import Transition from '../../components/Transition/Transition';
import Position from '../../types/enum/Position';
import './Popover.scss';
import IPopoverProps from './PopoverProps';

const Popover = ({
  canFlip,
  getTargetNode,
  offset,
  position,
  isOpen,
  transitionName,
  speedIn,
  speedOut,
  children,
  className,
}: IPopoverProps) => {
  const popover = useRef<HTMLDivElement | null>();
  const popper = useRef<PopperInstance>();

  useEffect(() => {
    if (isOpen) {
      renderPopover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (popper.current) {
        popper.current.destroy();
      }
    };
  }, []);

  function renderPopover() {
    const currentRef = getTargetNode();
    const elt = currentRef ? (currentRef instanceof Element ? currentRef : currentRef.current) : undefined;
    if (popover.current && elt) {
      popper.current = createPopper(elt, popover.current, {
        placement: position,
        modifiers: compact([
          preventOverflow,
          canFlip ? flip : undefined,
          { name: 'offset', options: { offset: [0, offset] } },
        ]),
      });
    }
  }

  const classes = classNames(
    'popover',
    {
      [`popover-${position}`]: true,
    },
    className
  );

  return (
    <Portal>
      <Transition isOpen={isOpen} transitionName={transitionName} speedIn={speedIn} speedOut={speedOut}>
        <div className={classes} ref={(el) => (popover.current = el)}>
          <div className="inner-popover">{children}</div>
        </div>
      </Transition>
    </Portal>
  );
};

Popover.defaultProps = {
  position: Position.BOTTOM,
  offset: 4,
  transitionName: 'none',
  canFlip: true,
  speedIn: 500,
  speedOut: 0,
};

export default Popover;
